"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Portal } from "./Portal";

type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

type PwaCtx = {
  canInstall: boolean;
  isStandalone: boolean;
  install: () => Promise<void>;
  pushStatus: NotificationPermission | "unsupported";
  pushSubscribed: boolean;
  enablePush: () => Promise<boolean>;
  disablePush: () => Promise<boolean>;
};

const Ctx = createContext<PwaCtx>({
  canInstall: false,
  isStandalone: false,
  install: async () => {},
  pushStatus: "default",
  pushSubscribed: false,
  enablePush: async () => false,
  disablePush: async () => false,
});

export const usePwa = () => useContext(Ctx);

const PUSH_ASKED_KEY = "pwa-push-asked-date"; // "1"(한 번만) 대신 마지막으로 물어본 날짜(YYYY-MM-DD)를 저장 — 매일 재노출

function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function todayKST(): string {
  // 다른 "오늘" 계산(TodayFortuneHero 등)과 동일하게 Asia/Seoul 기준 날짜로 통일.
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function PwaProvider({ children }: { children: React.ReactNode }) {
  const [deferred, setDeferred] = useState<BIPEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [pushStatus, setPushStatus] = useState<NotificationPermission | "unsupported">("default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: "ok" | "err" } | null>(null);

  // 토스트 자동 사라짐(약 4초)
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4200);
    return () => clearTimeout(t);
  }, [toast]);

  // 서비스워커 등록 + 현재 구독 여부 파악
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setPushSubscribed(!!sub))
        .catch(() => {});
    }
    if (typeof Notification === "undefined" || !("PushManager" in window)) {
      setPushStatus("unsupported");
    } else {
      setPushStatus(Notification.permission);
    }
  }, []);

  // 설치 프롬프트 캡처
  useEffect(() => {
    const onBIP = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BIPEvent);
    };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // standalone(PWA) 감지
  useEffect(() => {
    const modes = ["standalone", "fullscreen", "minimal-ui"].map((m) => window.matchMedia(`(display-mode: ${m})`));
    const compute = () =>
      modes.some((m) => m.matches) || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    const update = () => setIsStandalone(compute());
    update();
    modes.forEach((m) => m.addEventListener?.("change", update));
    return () => modes.forEach((m) => m.removeEventListener?.("change", update));
  }, []);

  // 모바일 기기로 접속 시(PWA 설치 여부 무관) 푸시 미설정이면 하루 한 번 옵트인 팝업.
  // 이미 허용/차단(default가 아님)했으면 다시 묻지 않는다 — 브라우저가 재요청도 막음.
  useEffect(() => {
    if (!isMobileDevice()) return;
    if (pushStatus !== "default") return;
    let lastAsked = "";
    try {
      lastAsked = localStorage.getItem(PUSH_ASKED_KEY) || "";
    } catch {}
    if (lastAsked !== todayKST()) {
      const t = setTimeout(() => setShowModal(true), 800);
      return () => clearTimeout(t);
    }
  }, [pushStatus]);

  const install = useCallback(async () => {
    if (!deferred) return;
    await deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {}
    setDeferred(null);
  }, [deferred]);

  const enablePush = useCallback(async (): Promise<boolean> => {
    try {
      if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) {
        setToast({ kind: "err", msg: "이 환경에서는 알림을 지원하지 않아요. iPhone은 홈 화면에 설치 후 사용해 주세요." });
        return false;
      }
      const permission = await Notification.requestPermission();
      setPushStatus(permission);
      if (permission !== "granted") {
        setToast({ kind: "err", msg: "알림이 허용되지 않았어요. 기기 설정에서 ALIGN 알림을 켜주세요." });
        return false;
      }

      const vapidRes = await fetch("/api/push/vapid");
      const { publicKey } = await vapidRes.json();
      if (!publicKey) {
        setToast({ kind: "err", msg: "알림 설정에 실패했어요. 잠시 후 다시 시도해 주세요." });
        return false;
      }

      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const sub =
        existing ||
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        }));

      await fetch("/api/push/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sub),
      });
      setPushSubscribed(true);
      setToast({ kind: "ok", msg: "🔔 알림이 켜졌어요! 확인 알림을 방금 보냈어요." });
      return true;
    } catch {
      setToast({ kind: "err", msg: "알림 설정 중 문제가 생겼어요. 다시 시도해 주세요." });
      return false;
    }
  }, []);

  const disablePush = useCallback(async (): Promise<boolean> => {
    try {
      if (!("serviceWorker" in navigator)) return false;
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch("/api/push/unsubscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {});
        await sub.unsubscribe().catch(() => {});
      }
      setPushSubscribed(false);
      setToast({ kind: "ok", msg: "알림을 껐어요. 언제든 다시 켤 수 있어요." });
      return true;
    } catch {
      setToast({ kind: "err", msg: "알림 끄기에 실패했어요. 다시 시도해 주세요." });
      return false;
    }
  }, []);

  const dismissModal = useCallback(() => {
    try {
      localStorage.setItem(PUSH_ASKED_KEY, todayKST());
    } catch {}
    setShowModal(false);
  }, []);

  return (
    <Ctx.Provider value={{ canInstall: !!deferred, isStandalone, install, pushStatus, pushSubscribed, enablePush, disablePush }}>
      {children}
      {toast && (
        <Portal>
        <div
          role="status"
          onClick={() => setToast(null)}
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top) + 14px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10000,
            maxWidth: "88vw",
            padding: "12px 18px",
            borderRadius: 14,
            fontSize: 14,
            fontWeight: 500,
            textAlign: "center",
            lineHeight: 1.5,
            color: "var(--ink)",
            background: "var(--card)",
            border: `1px solid ${toast.kind === "ok" ? "rgba(58,74,90,0.45)" : "var(--line-strong)"}`,
            boxShadow: "0 8px 30px rgba(0,0,0,0.45)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            cursor: "pointer",
          }}
        >
          {toast.msg}
        </div>
        </Portal>
      )}
      {showModal && (
        <Portal>
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}
          onClick={dismissModal}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 420, margin: 12, borderRadius: 22, background: "var(--card)", border: "1px solid var(--line-strong)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", padding: "26px 22px 22px", boxShadow: "0 -8px 40px rgba(0,0,0,0.4)" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--accent-soft)", border: "1px solid rgba(58,74,90,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="var(--gold-bright)" strokeWidth={1.6}><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9z" /><path d="M10.5 21a1.5 1.5 0 003 0" /></svg>
              </div>
            </div>
            <div style={{ textAlign: "center", fontSize: 18, fontWeight: 500, color: "var(--ink)" }}>오늘의 운세를 알림으로 받아보세요</div>
            <p style={{ textAlign: "center", fontSize: 13.5, color: "var(--ink-dim)", lineHeight: 1.6, margin: "10px 0 20px" }}>
              매일 아침 일진과 중요한 운의 흐름을 푸시 알림으로 보내드려요. 설정에서 언제든 끌 수 있어요.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <button onClick={async () => { await enablePush(); dismissModal(); }}
                style={{ padding: "13px 0", borderRadius: 12, border: "1px solid rgba(58,74,90,0.4)", cursor: "pointer", background: "var(--accent-soft)", color: "var(--gold-bright)", fontSize: 15, fontWeight: 500 }}>
                알림 허용하기
              </button>
              <button onClick={dismissModal}
                style={{ padding: "11px 0", borderRadius: 12, border: "none", cursor: "pointer", background: "transparent", color: "var(--ink-faint)", fontSize: 13.5 }}>
                나중에 할게요
              </button>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </Ctx.Provider>
  );
}
